import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/dist/Draggable';
import Masonry from 'masonry-layout';
import { ThemeContext } from 'styled-components';

import { ReactComponent as DraggableIcon } from '@pkg/components/Sections/Profile/icons/draggable.svg';
import { useAppSelector } from '@pkg/hooks';
import useDragScroll, { type DragToScrollCallback } from '@pkg/hooks/useDragScroll';
import { useMediaQuery } from '@pkg/providers';

import { GridItem } from '@app/pages/Prototype/Grid/GridItem/GridItem';
import { type YatPageState } from '@app/store/slices/yat-page/types';
import { type ContentItem, ContentType, type NFTContent } from '@app/store/types/yat-page-actions';

import { NftPlaceholderCard } from '../../NftManagerPanel/components';
import { PUBLISH_BAR_HEIGHT } from '../../styles';
import YatPreview from '../Preview';

import { DraggableIconWrapper, DraggableMasonryWrapper, GridItemInner, GridItemOuter } from './styles';

gsap.registerPlugin(Draggable);

const GRID_ITEM_WRAPPER_CLASS_NAME = 'yat-page-draggable-grid-item';
const formatId = (id: string) => `${GRID_ITEM_WRAPPER_CLASS_NAME}-${id}`;
const getIdFromFormattedId = (id: string) => id.replace(`${GRID_ITEM_WRAPPER_CLASS_NAME}-`, '');

// Class name that can be used ignore draggable events being fired if clicked
export const DRAGGABLE_CLASS_NAME_TO_IGNORE = 'prevent-draggable';
const LOADING_ITEMS = Array(20).fill(null);

interface Props {
    columns: number;
    columnGutter: number;
    editMode?: boolean;
    isLoading?: boolean;
    onDragStart: (draggingId: string) => void;
    onDrag: (targetId: string) => void;
    onDragEnd: () => void;
    onDragToScroll: DragToScrollCallback;
    assets: YatPageState['pageAssets'];
}

const YatDraggableMasonry = ({
    columns,
    columnGutter,
    editMode,
    isLoading,
    onDragStart,
    onDrag,
    onDragEnd,
    onDragToScroll,
    assets: pageAssets,
}: Props) => {
    const theme = useContext(ThemeContext);
    const draggingId = useAppSelector((state) => state.yatPage.draggingId);
    const { usingMouse } = useMediaQuery();
    const dragToScroll = useDragScroll<HTMLDivElement>(onDragToScroll, { disabled: !usingMouse || editMode });
    const parentRef = dragToScroll.ref;
    const masonryRef = useRef<Masonry>();
    const resizeObserverRef = useRef<ResizeObserver>();
    const draggableRef = useRef<Draggable>();
    const dragStartedRef = useRef(false);
    const preventReorderRef = useRef<NodeJS.Timeout>();
    const underIdRef = useRef<string>();
    const canReorderRef = useRef<boolean>(false);
    const [draggingOriginIndex, setDraggingOriginIndex] = useState<number>();
    const edgeCounterRef = useRef(0);
    const currentPageItemsLength = useRef(pageAssets.sorted.length);
    const currentToggledPageItemsLength = useRef(0);

    const draggable = useMemo(
        () => editMode && !isLoading && pageAssets.sorted.length > 1,
        [editMode, isLoading, pageAssets.sorted.length],
    );

    useEffect(() => {
        if (!parentRef.current) return;

        // On first load items won't have loaded yet, so ignore layout calculations
        let initLayout = false;

        // If masonry already exists...
        if (masonryRef.current) {
            // Destroy before re-creating new instance and update layout
            masonryRef.current.destroy();
            initLayout = true;
        }

        masonryRef.current = new Masonry(parentRef.current, {
            itemSelector: `.${GRID_ITEM_WRAPPER_CLASS_NAME}`,
            gutter: columnGutter,
            initLayout,
            horizontalOrder: true,
            // todo: Using 'transitionDuration' causes an overlapping bug on drag. To get around this we're animating
            // changes by transitioning 'top' / 'left' which isn't ideal (not hardware accelerated). Ideally find a way
            // to fix overlapping issue caused be this (as this uses transform based animations)
            transitionDuration: 0,
        });

        return () => {
            masonryRef.current?.destroy();
        };
    }, [parentRef, columnGutter, theme]);

    const updateMasonryLayout = () => {
        if (!masonryRef.current) return;

        // On dom changes (new children or children order changed) reload masonry items
        masonryRef.current.reloadItems();
        masonryRef.current.layout();
    };

    useEffect(() => {
        if (resizeObserverRef.current) return;

        // If any entity updates, update masonry layout
        resizeObserverRef.current = new ResizeObserver(updateMasonryLayout);

        return () => {
            resizeObserverRef.current?.disconnect();
        };
    }, []);

    // When new item is added / removed, update masonry layout
    useEffect(() => {
        if (!parentRef.current) return;

        updateMasonryLayout();

        // On content height resize (i.e. on lazy load), listen for changes
        const children = Array.from(parentRef.current.children);
        children.forEach((child) => {
            const childOfChild = child.children[0];
            if (!childOfChild) return;

            resizeObserverRef.current?.observe(childOfChild);
        });

        // If a new item has been added to grid, it gets added to the beginning, so scroll last item into view
        // Check if new toggled assets to prevent scroll on pagination add more
        const toggledAssets = Object.values(pageAssets.toggled).reduce(
            (prev, value) => (value == 'added' ? prev + 1 : prev),
            0,
        );

        const newToggledAssets = toggledAssets > currentToggledPageItemsLength.current;

        const newPageAssets =
            currentPageItemsLength.current !== 0 && pageAssets.sorted.length > currentPageItemsLength.current;

        if (newToggledAssets && newPageAssets) {
            const firstChild = children[0];
            firstChild.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }

        currentPageItemsLength.current = pageAssets.sorted.length;
        currentToggledPageItemsLength.current = toggledAssets;
    }, [parentRef, pageAssets.sorted.length, isLoading, pageAssets.toggled]);

    const elementIdAtPoint = (x: number, y: number, draggingId: string) => {
        const elements = document.elementsFromPoint(x, y);
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i] as HTMLDivElement;
            const elementId = getIdFromFormattedId(element.id);
            if (element.classList.contains(GRID_ITEM_WRAPPER_CLASS_NAME) && elementId !== draggingId) {
                return elementId;
            }
        }
    };

    const getXAndYFromEvent = (event: MouseEvent | TouchEvent) => {
        // Get mouse event position
        if (event instanceof MouseEvent) {
            const { clientX, clientY } = event;
            return { x: clientX, y: clientY };
        }

        // Else, get touch event position
        const { pageX, pageY } = event.touches[0];
        return { x: pageX, y: pageY };
    };

    const handleDraggingAtVerticalEdges = useCallback(
        (event: MouseEvent | TouchEvent, draggingElement: Element) => {
            const { y: eventY } = getXAndYFromEvent(event);

            if (parentRef.current) {
                const { height, y } = parentRef.current.getBoundingClientRect();

                // Ensure that scroll does not occur far beyond last grid item
                if (height - Math.abs(y) < window.innerHeight / 1.5) {
                    return;
                }
            }

            const draggingBelow = eventY > window.innerHeight - PUBLISH_BAR_HEIGHT * 1.5;
            const draggingAbove = eventY < PUBLISH_BAR_HEIGHT * 2;

            if (draggingBelow || draggingAbove) {
                draggingElement.scrollIntoView({
                    behavior: 'smooth',
                    block: draggingAbove ? 'end' : 'start',
                    inline: 'nearest',
                });
            }
        },
        [parentRef],
    );

    const updateDraggableElementPosition = () => {
        if (!draggableRef.current) return;

        // Update draggable item transform relative to draggable position and cursor
        draggableRef.current.update(false, true);
    };

    const resetCurrentReorderTimeout = () => {
        if (preventReorderRef.current === undefined) return;

        clearTimeout(preventReorderRef.current);
        preventReorderRef.current = undefined;
    };

    const handleDrag = useCallback(
        (event: MouseEvent | TouchEvent, draggingIndex: number, draggingId: string, draggingElement: Element) => {
            if (!draggableRef.current) return;

            // If drag started...
            if (!dragStartedRef.current) {
                setDraggingOriginIndex(draggingIndex);
                onDragStart(draggingId);
                dragStartedRef.current = true;

                gsap.to(draggingElement, {
                    scale: 1.1,
                    filter: `drop-shadow(0 0 44px ${theme.shades.black[85]})`,
                    duration: theme.transitions.speeds.normal,
                    ease: 'power2.out',
                });

                return;
            }

            // If there's an element to be swapped, and currently allowed to reorder (have waited minimum delay)...
            if (underIdRef.current && canReorderRef.current) {
                onDrag(underIdRef.current);
                updateMasonryLayout();
                updateDraggableElementPosition();
                canReorderRef.current = false;
                return;
            }

            // Scroll viewport if at edge of screen
            if (edgeCounterRef.current % 2 === 0) handleDraggingAtVerticalEdges(event, draggingElement);
            edgeCounterRef.current = edgeCounterRef.current + 1;

            // Get element currently under cursor
            const { x, y } = getXAndYFromEvent(event);
            const underId = elementIdAtPoint(x, y, draggingId);

            // If item under is different to previous item, reset
            if (underIdRef.current !== underId) {
                underIdRef.current = underId;
                resetCurrentReorderTimeout();
            }

            // If not hovering over any item, ignore rest
            if (underIdRef.current === undefined) {
                // User might have scrolled, so update current element position
                updateDraggableElementPosition();
                return;
            }

            // If there's a timeout active currently, ignore rest
            if (preventReorderRef.current !== undefined) return;

            // Set delay before another swap can happen
            preventReorderRef.current = setTimeout(() => {
                canReorderRef.current = true;
                preventReorderRef.current = undefined;
            }, theme.transitions.speeds.fast * 1000);
        },
        [
            handleDraggingAtVerticalEdges,
            onDrag,
            onDragStart,
            theme.shades.black,
            theme.transitions.speeds.fast,
            theme.transitions.speeds.normal,
        ],
    );

    const handleDragEnd = (draggingElement: Element) => {
        // Only emit event if drag started...
        if (dragStartedRef.current) {
            onDragEnd();
            dragStartedRef.current = false;
            underIdRef.current = undefined;

            if (preventReorderRef.current !== undefined) {
                clearTimeout(preventReorderRef.current);
                preventReorderRef.current = undefined;
            }
        }

        const handleComplete = () => {
            if (!draggableRef.current) return;

            draggableRef.current.kill();
            draggableRef.current = undefined;
        };

        // Reset to origin on release
        gsap.to(draggingElement, {
            x: 0,
            y: 0,
            scale: 1,
            filter: 'none',
            duration: theme.transitions.speeds.normal,
            ease: 'power2.out',
            onComplete: handleComplete,
        });
    };

    const createDraggable = (
        event: React.MouseEvent | React.PointerEvent,
        draggingIndex: number,
        draggingId: string,
    ) => {
        if (!draggable || !parentRef.current) return;

        // If draggable already exists, kill ref before re-creating new instance
        if (draggableRef.current) draggableRef.current.kill();

        const nativeEvent = event.nativeEvent;

        // Prevent triggering drag if clicking non-draggable element
        const clickedIgnoredClassName = (nativeEvent.target as Element).classList.contains(
            DRAGGABLE_CLASS_NAME_TO_IGNORE,
        );
        if (clickedIgnoredClassName) return;

        const child = parentRef.current.children[draggingIndex];

        // Parent has masonry transform applied, so we target child of child to apply different transforms
        const childOfChild = child.children[0];
        if (!childOfChild) return;

        // Create unique draggable instance
        draggableRef.current = new Draggable(childOfChild, {
            zIndexBoost: false,
            allowContextMenu: true,
            onDrag: (event: MouseEvent | PointerEvent) => handleDrag(event, draggingIndex, draggingId, childOfChild),
            onDragEnd: () => handleDragEnd(childOfChild),
        });

        // Start dragging
        draggableRef.current.startDrag(nativeEvent);
    };

    const itemWidth = useMemo(
        () => `calc(${100 / columns}% - ${(columnGutter / columns) * (columns - 1)}px)`,
        [columnGutter, columns],
    );

    return (
        <DraggableMasonryWrapper {...dragToScroll}>
            <AnimatePresence>
                {pageAssets.sorted.map((itemId, index) => {
                    if (!itemId) return; // For cases where sort is not sequential on DB
                    const formattedId = formatId(itemId);
                    const dragging = draggingId === itemId;

                    // Auto Generate Profiles might add assets with no image or video url
                    const asset = pageAssets.draft[itemId].asset;
                    if (asset.type === ContentType.NFT) {
                        const nftContent = asset as ContentItem<NFTContent>;
                        if (!(nftContent.content?.animation_url || nftContent?.content?.image)) return;
                    }

                    return (
                        <GridItemOuter
                            key={formattedId}
                            id={formattedId}
                            className={GRID_ITEM_WRAPPER_CLASS_NAME}
                            $width={itemWidth}
                            $dragging={dragging}
                            exit={{
                                scale: 0.8,
                                opacity: 0,
                                transition: {
                                    duration: theme.transitions.speeds.normal,
                                    ease: theme.transitions.eases.on,
                                },
                            }}
                        >
                            <YatPreview show={draggingOriginIndex !== index && dragging} hasExitAnimation={false} />
                            <GridItemInner
                                $draggable={draggable}
                                onMouseDown={(event) => usingMouse && createDraggable(event, index, itemId)}
                            >
                                <GridItem item={pageAssets.draft[itemId].asset} editMode={editMode} />
                                {draggable && !usingMouse && (
                                    <DraggableIconWrapper
                                        $type={pageAssets.draft[itemId].asset.type}
                                        onPointerDown={(event) => createDraggable(event, index, itemId)}
                                    >
                                        <DraggableIcon width={12} />
                                    </DraggableIconWrapper>
                                )}
                            </GridItemInner>
                        </GridItemOuter>
                    );
                })}
                {isLoading &&
                    LOADING_ITEMS.map((_item, index) => (
                        <GridItemOuter
                            key={`outer${index}`}
                            $width={itemWidth}
                            className={GRID_ITEM_WRAPPER_CLASS_NAME}
                            exit={{
                                scale: 0.8,
                                opacity: 0,
                                transition: {
                                    duration: theme.transitions.speeds.normal,
                                    ease: theme.transitions.eases.on,
                                },
                            }}
                        >
                            <GridItemInner $draggable={false}>
                                <NftPlaceholderCard custom={index} />
                            </GridItemInner>
                        </GridItemOuter>
                    ))}
            </AnimatePresence>
        </DraggableMasonryWrapper>
    );
};

export default YatDraggableMasonry;
